import { Button, Textarea, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import MediaFileDropzone from '../MediaFileDropzone'
import { useBucketInfo } from '../../hooks/useBucketInfo'
import { GEOXP_AUDIO_BUCKET, GEOXP_VIDEO_BUCKET, SPOT_MEDIA_TYPES } from '../../utils/constants/db.constants'
import { useDarkMode } from '../../contexts/DarkModeContext'
import { LocalizationDraft, localizationHasContent } from './useLocalizations'
import AgamiMediaTypeChip from '../AgamiMediaTypeChip'

interface LocalizationFieldsProps {
  draft: LocalizationDraft
  /** The label of the language, for the removal confirm and the aria labels. */
  languageLabel: string
  /** The SPOT's media type: one type per agami, across every language. */
  contentMediaType: number
  uploading: boolean
  onDescriptionChange: (value: string) => void
  onMediaFileChange: (file: File | null) => void
  onRemove: () => void
}

/**
 * Description and media of ONE additional language (08.2). The fallback note is
 * the only place the fallback of 08.3 becomes visible to whoever writes: an
 * empty tab still explains the read fallback before the author fills it.
 */
const LocalizationFields: React.FC<LocalizationFieldsProps> = ({
  draft,
  languageLabel,
  contentMediaType,
  uploading,
  onDescriptionChange,
  onMediaFileChange,
  onRemove,
}) => {
  const { t } = useTranslation()
  const { darkMode } = useDarkMode()

  const bucketInfo = useBucketInfo({
    bucket: contentMediaType === 2 ? GEOXP_VIDEO_BUCKET : GEOXP_AUDIO_BUCKET,
    enabled: contentMediaType !== SPOT_MEDIA_TYPES.NONE,
  })

  return (
    <div className="flex flex-col gap-4" data-testclass="localization-fields">
      {!localizationHasContent(draft) && (
        <Typography
          variant="small"
          className="text-gray-600 dark:text-base-gray-500"
          data-testclass="localization-fallback-note"
        >
          {t('localizations.fallbackNote')}
        </Typography>
      )}

      <div className="grid grid-cols-2 gap-4" data-testclass="language-content-fields">
        <div
          className="min-w-0 w-full h-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4"
          data-testclass="language-media-card"
        >
          <Typography className="font-bold text-primary text-xl font-barlow">
            {t('upload.mediaFile.title')}
          </Typography>
          {contentMediaType === SPOT_MEDIA_TYPES.NONE ? (
            <AgamiMediaTypeChip mediaType={contentMediaType} dataTestClass="intentional-no-media" />
          ) : (
            <MediaFileDropzone
              editMode={Boolean(draft.existing?.audio_content_path)}
              mediaType={contentMediaType}
              prevFileLabel={
                draft.existing?.audio_filename ?? draft.existing?.audio_content_path ?? undefined
              }
              prevFileUrl={draft.existing?.mediaUrl}
              mediaFile={draft.mediaFile}
              setMediaFile={onMediaFileChange}
              uploadSizeLimit={bucketInfo.uploadSizeLimit ?? 0}
              allowedFileTypes={bucketInfo.allowedFileTypes}
            />
          )}
        </div>

        <div
          className="min-w-0 w-full h-full flex flex-col bg-white dark:bg-primary rounded-lg p-4 shadow-sm gap-4"
          data-testclass="language-description-card"
        >
          <Typography className="font-bold text-primary text-xl font-barlow">
            {t('uploadAgamiInterface.descriptionLabel')}
          </Typography>
          <Textarea
            className="w-full text-primary-600 focus:text-primary-700 dark:!text-base-gray-500 dark:focus:text-base-gray-400"
            rows={4}
            value={draft.description}
            onChange={(e) => onDescriptionChange(e.currentTarget.value)}
            label={t('localizations.descriptionLabel', { language: languageLabel })}
            disabled={uploading}
            data-testclass="localization-description-input"
            {...(darkMode && { color: 'blue-gray' })}
            labelProps={{
              className:
                'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
            }}
          />
        </div>
      </div>

      <div>
        <Button
          size="sm"
          color="red"
          variant="outlined"
          onClick={onRemove}
          disabled={uploading}
          data-testclass="localization-remove-btn"
        >
          {t('localizations.removeLanguage', { language: languageLabel })}
        </Button>
      </div>
    </div>
  )
}

export default LocalizationFields
